import Cookies from "js-cookie";
import { FundHolding } from "./dtos/fund-holdings";
import config from "./config";
import { FundHoldingSchema } from "./dtos/fund-holding-schema";
import { useQuery } from "@tanstack/react-query";
import { ResponseError } from "../types/responseError";

export const fundHoldingsByTokenIdRequest = async ({
  tokenId,
  page,
  pageSize,
  sourceDate,
}: {
  tokenId: string;
  page: number;
  pageSize: number;
  sourceDate?: string;
}) => {
  const auth = Cookies.get("auth-token");

  const url = new URL(
    `${config.GW_BASE_URL}/v1/token-fund-holdings/${tokenId}`,
  );
  url.searchParams.append("page", page.toString());
  url.searchParams.append("limit", pageSize.toString());
  if (sourceDate) {
    url.searchParams.append("sourceDate", sourceDate);
  }

  const request = await fetch(url, {
    credentials: "include",
    headers: {
      authorization: auth ?? "",
    },
  });

  if (!request.ok) {
    console.error(await request.text());
    throw new ResponseError("Error fetching fund schema information", request);
  }

  return request.json() as Promise<{
    data: FundHolding[];
    total: number;
  }>;
};

export const useFundSchemaByTokenIdQuery = ({ tokenId }: { tokenId: string }) =>
  useQuery<FundHoldingSchema, ResponseError>({
    queryKey: ["token-schema", tokenId],
    queryFn: async () => {
      const response = await fetch(
        `${config.GW_BASE_URL}/v1/token-fund-holdings/${tokenId}/schema`,
        {
          credentials: "include",
          headers: {
            authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );

      if (!response.ok) {
        throw new ResponseError(
          "Error fetching fund schema information",
          response,
        );
      }

      return response.json() as Promise<FundHoldingSchema>;
    },
  });

export const getHoldingsFilePresignedUrl = async ({
  tokenId,
  type,
  sourceDate,
}: {
  tokenId: string;
  type: "csv" | "pdf";
  sourceDate?: string | null | undefined;
}) => {
  const url = new URL(
    `${config.GW_BASE_URL}/v1/token-fund-holdings/${tokenId}/download/${type}`,
  );
  if (sourceDate) {
    url.searchParams.append("sourceDate", sourceDate);
  }
  const result = await fetch(url, {
    credentials: "include",
    headers: {
      authorization: Cookies.get("auth-token") ?? "",
    },
  });

  if (!result.ok) {
    throw new ResponseError(
      "Error fetching holdings file presigned url",
      result,
    );
  }

  return result.json() as Promise<{ url: string }>;
};

export const useFundHoldingsDateRangeQuery = ({
  tokenId,
}: {
  tokenId: string;
}) =>
  useQuery<{ dates: string[] }, ResponseError>({
    queryKey: ["fund-holdings-date-range", tokenId],
    queryFn: async () => {
      const response = await fetch(
        `${config.GW_BASE_URL}/v1/token-fund-holdings/${tokenId}/download/date-range`,
        {
          credentials: "include",
          headers: {
            authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );

      if (!response.ok) {
        throw new ResponseError(
          "Error fetching fund schema information",
          response,
        );
      }

      return response.json() as Promise<{ dates: string[] }>;
    },
    retry: 1,
  });
